import { LoadingScreen } from '@/components/LoadingScreen.tsx';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { useAppConfig } from '@/appConfig/useAppConfig.ts';
import { translate } from '@/translation/translate.ts';
import { useForm } from '@/form/useForm.ts';
import { Header } from '@/components/layout/Header.tsx';
import { TransitionGroup } from 'react-transition-group';
import { SlideRenderer } from '@/components/SlideRenderer.tsx';
import { Navigation } from '@/components/Navigation.tsx';
import { LayerDisplay } from '@/layers/LayerDisplay.tsx';
import { SidePanel } from '@/sidePanel/SidePanel.tsx';
import { SystemBanner } from '@/components/SystemBanner.tsx';
import { SlideWrapper } from '@/components/layout/SlideWrapper.tsx';
import { useLanguage } from '@/eventConfig/LanguageContext.tsx';
import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { getDevice } from '@/util/getDevice.ts';
import { isNil } from 'ramda';
import { i18n } from '@/translation/TranslationProvider.tsx';
import usePiwikProInit from './util/usePiwik';

export const App = () => {
  const appConfig = useAppConfig();
  const { eventConfig } = useEventConfig();
  const formControl = useForm();
  const navControl = useNavigation();

  usePiwikProInit(eventConfig?.meta.country);

  // ----

  const [showSidePanel, setShowSidePanel] = useState<boolean>(false);

  const setup = () => {
    // setup Sentry
    const device = getDevice();

    if (device) {
      Sentry.setUser({
        id: device.name,
        ...device,
      });
    }

    // override system back button functionality
    window.history.pushState(null, 'null', window.location.href);

    // ask before leaving the page
    window.addEventListener('beforeunload', (event) => {
      if (!formControl.isEmpty()) {
        // message is not shown in most browsers
        event.returnValue = 'Are you sure you want to leave?';
      }
    });
  };

  useEffect(() => {
    setup();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const { locale } = useLanguage();

  useEffect(() => {
    if (!isNil(locale)) {
      i18n.activate(locale);
    }
  }, [locale]);

  const handleShowSidePanel = () => {
    setShowSidePanel((v) => !v);
  };

  const templateData = {
    form: formControl.formData,
    ...eventConfig,
  };

  if (appConfig === null || eventConfig === null || navControl.currentSlide === null) {
    return <LoadingScreen message={translate(eventConfig?.translations.loading)} />;
  }

  return (
    <SlideWrapper sidePanelVisible={showSidePanel}>
      <header>
        <Header templateData={templateData} showSidePanel={handleShowSidePanel} />
      </header>

      <TransitionGroup component="div" role="main" className="Stage h-100">
        <SlideRenderer />
      </TransitionGroup>

      <nav>
        <Navigation />
      </nav>

      <LayerDisplay templateData={templateData} />

      <SidePanel sidePanelVisible={showSidePanel} setSidePanelVisible={setShowSidePanel} />

      <SystemBanner text={translate(eventConfig.meta.banner)} />
    </SlideWrapper>
  );
};
